import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react'
import { useNewTranslation } from 'hooks/index'
import { useSelector, useDispatch } from 'react-redux'
import TextParticle from 'particles/Text'
import { State } from 'state'
import { TagOption } from 'state/constants'
import {
    makeCommentTagBadgesSelector,
    selectSelectedSubmissionView,
    makeShowCommentSelector,
    SubmissionProgressProps,
    makeSubmissionContentSelector,
    openCaptionDetailModal,
    selectSelectedCampaignSummary,
    makeShowLinkInBioSelector,
} from 'state/review'
import { SUBMISSION_VIEW_ENUM } from 'state/submissions'
import { StepState, TaskState } from 'state/tasks'
import { theme } from 'theme'
import RoundTag, { TagContentType } from 'particles/Tag'
import styled from 'particles/themes/styled'
import { useTranslation } from 'react-i18next'
import LinkInBioWithTooltip from './LinkInBioWithTooltip'
import _ from 'lodash'

interface CommentProps {
    submissionProgress: SubmissionProgressProps
    step: StepState
    task?: TaskState
}

const CommentWithReadMore = styled.div`
    width: 100%;
    text-align: left;
    position: relative;
    word-break: break-word;
    white-space: pre-wrap;
    min-height: 3.4em; /* Fixed height for 3 lines with 100% line-height */
    line-height: 1.5;
    .comment-text {
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        min-height: 3.4em; /* Fixed height for 3 lines with 1.5 line-height */
        line-height: 1.5;
    }
`

const ReadMoreButton = styled(TextParticle)`
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
`

const CommentText = styled(TextParticle)`
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    min-height: 3.4em; /* Fixed height for 3 lines with 1.5 line-height */
    line-height: 1.5;
`

const ReadMoreWrapper = styled.div`
    position: absolute;
    bottom: 0;
    right: 0;
    display: flex;
    align-items: center;
    background: linear-gradient(to right, rgba(255, 255, 255, 0), white 15%, white);
    padding-left: 1rem;
`

const ReadMoreEllipsis = styled(TextParticle)`
    margin-right: 4px;
`

const CommentTextFixed = styled(TextParticle)`
    text-align: left;
    word-break: break-word;
    white-space: pre-wrap;
    min-height: 3.4em; /* Fixed height for 3 lines with 1.5 line-height */
    line-height: 1.5;
`

const Comment: React.FC<CommentProps> = ({ submissionProgress, step, task }) => {
    const [newT] = useNewTranslation()
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const selectedCampaignSummary = useSelector(selectSelectedCampaignSummary)
    const selectShowComment = useMemo(makeShowCommentSelector, [])
    const showComment = useSelector((state: State) => selectShowComment(state, { step }))
    const selectTagBadges = useMemo(makeCommentTagBadgesSelector, [])
    const tagBadges = useSelector((state: State) => selectTagBadges(state, { submissionProgress }))
    const selectShowLinkInBio = useMemo(makeShowLinkInBioSelector, [])
    const showLinkInBioFeature = useSelector((state: State) => selectShowLinkInBio(state, { step, task }))

    // Get submission content for the modal
    const selectSubmissionContents = useMemo(makeSubmissionContentSelector, [])
    const { submissionContent, captionData } = useSelector((state: State) =>
        selectSubmissionContents(state, {
            submissionProgress,
            step,
        }),
    )

    // For PENDING view, caption is not extracted by selector, so get it directly from submissionProgress.data
    const isPendingView = selectedSubmissionView === SUBMISSION_VIEW_ENUM.PENDING
    const pendingCaption = isPendingView ? submissionProgress.data?.caption : null
    const displayCaption = pendingCaption || captionData

    // State and ref for tracking if comment exceeds 3 lines
    const [showReadMore, setShowReadMore] = useState(false)
    const commentRef = useRef<HTMLDivElement>(null)

    // Helper function to check if text exceeds 3 lines
    const checkTextExceedsLines = useCallback((ref: React.RefObject<HTMLDivElement>) => {
        if (!ref.current) return false
        const lineHeight = parseFloat(getComputedStyle(ref.current).lineHeight)
        const height = ref.current.scrollHeight
        const lines = Math.round(height / lineHeight)
        return lines > 3
    }, [])

    // Determine comment, title, and contentType
    let title = ''
    let contentType: TagContentType = 'neutral'
    let comment = submissionProgress.comment

    if (selectedSubmissionView === SUBMISSION_VIEW_ENUM.COMPLETED) {
        title = newT('submission.review.approvalCommentHeader', 'Approval comments')
        contentType = 'positive'
    } else if (
        selectedSubmissionView === SUBMISSION_VIEW_ENUM.FOR_REVIEW &&
        submissionProgress.pending_action !== null
    ) {
        const { pending_action: pendingAction } = submissionProgress
        if (pendingAction.action_type === '1') {
            // Pending approve
            contentType = 'positive'
            title = newT('submission.review.approvalCommentHeader', 'Approval comments')
        } else if (pendingAction.action_type === '3') {
            // Pending request edits
            contentType = 'warning'
            title = newT('submission.review.reasonsForRequestEditTitle', 'Reasons for Requesting Edits')
        } else if (pendingAction.action_type === '2') {
            // Pending reject
            contentType = 'negative'
            title = newT('submission.review.reasonsForRejectionTitle', 'Reasons for Rejection')
        }
        comment = pendingAction.data.comment
    } else if (selectedSubmissionView === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED) {
        title = newT('submission.review.reasonsForRequestEditTitle', 'Reasons for Requesting Edits')
        contentType = 'warning'
    }

    // Check if comment exceeds 3 lines (only for COMPLETED view)
    useEffect(() => {
        if (selectedSubmissionView === SUBMISSION_VIEW_ENUM.COMPLETED && commentRef.current && comment) {
            setShowReadMore(checkTextExceedsLines(commentRef))
        }
    }, [comment, selectedSubmissionView, checkTextExceedsLines])

    // Handler to open caption detail modal with comment
    const handleOpenCommentModal = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation()
            dispatch(
                openCaptionDetailModal({
                    isOpen: true,
                    caption: displayCaption || '',
                    mediaData: submissionContent,
                    comment: comment || '',
                    timestamp: submissionProgress.submitted_at,
                    country: selectedCampaignSummary?.country,
                }),
            )
        },
        [
            dispatch,
            submissionContent,
            displayCaption,
            comment,
            submissionProgress.submitted_at,
            selectedCampaignSummary?.country,
        ],
    )

    // Get Link in Bio URL
    const isStaticLinkInBioType = task?.data?.link_in_bio_type === 1

    let linkInBioUrl: string | undefined
    if (isStaticLinkInBioType) {
        linkInBioUrl = task.data['link_in_bio_url']
    } else if (task?.data?.link_in_bio_type === 2 && !!submissionProgress?.submission_task) {
        linkInBioUrl = submissionProgress.submission_task.data['link_in_bio_url']
    }

    // Show Link in Bio for PENDING, COMPLETED, and EDITS_REQUESTED views when feature is enabled and URL exists
    const shouldShowLinkInBio =
        selectedSubmissionView !== null &&
        [SUBMISSION_VIEW_ENUM.PENDING, SUBMISSION_VIEW_ENUM.COMPLETED, SUBMISSION_VIEW_ENUM.EDITS_REQUESTED].includes(
            selectedSubmissionView,
        ) &&
        showLinkInBioFeature &&
        !!linkInBioUrl

    if (!showComment && !shouldShowLinkInBio) {
        return null
    }

    return (
        <div>
            {showComment && (
                <div
                    style={{
                        borderTop: `1px solid ${theme.colors.governorBay}`,
                        borderBottom: `1px solid ${theme.colors.governorBay}`,
                        padding: `${theme.spacing.xxxxs} 0`,
                    }}
                >
                    <TextParticle scale="bodyS700">{title}</TextParticle>
                    {tagBadges && tagBadges.length > 0 && (
                        <div style={{ textAlign: 'left' }}>
                            {tagBadges.map((tag: TagOption) => {
                                return <RoundTag key={tag.uuid} contentType={contentType} label={tag.label} />
                            })}
                        </div>
                    )}
                    {selectedSubmissionView === SUBMISSION_VIEW_ENUM.COMPLETED && comment ? (
                        <CommentWithReadMore onClick={e => e.stopPropagation()}>
                            <CommentText scale="bodyS400" ref={commentRef}>
                                {comment}
                            </CommentText>
                            {showReadMore && (
                                <ReadMoreWrapper onClick={e => e.stopPropagation()}>
                                    <ReadMoreEllipsis color="secondary">...</ReadMoreEllipsis>
                                    <ReadMoreButton scale="bodyS400" color="skyBlue" onClick={handleOpenCommentModal}>
                                        {t('button.readMore')}
                                    </ReadMoreButton>
                                </ReadMoreWrapper>
                            )}
                        </CommentWithReadMore>
                    ) : (
                        <CommentTextFixed>
                            {comment ? comment : newT('submission.review.noSubmissionComment', 'None')}
                        </CommentTextFixed>
                    )}
                </div>
            )}
            {shouldShowLinkInBio && linkInBioUrl && <LinkInBioWithTooltip linkInBioUrl={linkInBioUrl} />}
        </div>
    )
}

export default Comment
